import * as React from 'react';

/**
 * Attaches an IntersectionObserver to a sentinel element.
 * When the sentinel becomes visible, calls fetchMore() if hasMore && !isFetching.
 * Returns the sentinelRef to attach to the sentinel DOM element.
 */
export const useInfiniteScroll = (
    hasMore: boolean,
    isFetching: boolean,
    fetchMore: () => void,
    scrollRootRef: React.RefObject<HTMLDivElement | null>,
): React.RefCallback<HTMLDivElement> => {
    const [sentinel, setSentinel] = React.useState<HTMLDivElement | null>(null);

    React.useEffect(() => {
        if (!sentinel) return undefined;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && hasMore && !isFetching) {
                    fetchMore();
                }
            },
            {
                root: scrollRootRef.current,
                threshold: 0.1,
            },
        );

        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [sentinel, hasMore, isFetching, fetchMore, scrollRootRef]);

    return setSentinel;
};
